import { api, queryKeys } from '@alpha/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { PageHeader } from '../../components/PageHeader';
import { Alert, Button, Card, FormGroup, Input, Spinner } from '../../ui';

const SYSTEM_TABS = [
  { label: 'News', to: '/system/news' },
  { label: 'Settings', to: '/system/settings' },
  { label: 'Cron Monitor', to: '/system/cron-log' },
];

/**
 * Settings are key/value rows rendered dynamically — the server owns the list,
 * so adding a setting is a migration, not a frontend change.
 *
 * Note for the API side: the legacy app stored settings like maintenance_mode
 * and withdrawal_days but never enforced them. Anything surfaced here should be
 * genuinely enforced server-side.
 */
export function SystemSettings() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.settings(),
    queryFn: () => api.admin.settings().then((r) => r.data),
  });

  const form = useForm<Record<string, string>>();

  const save = useMutation({
    mutationFn: (values: Record<string, string>) => api.admin.updateSettings(values),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.settings() }),
  });

  const testEmail = useMutation({ mutationFn: (to: string) => api.admin.sendTestEmail(to) });

  if (isLoading) return <Spinner />;

  return (
    <>
      <PageHeader title="Settings" tabs={SYSTEM_TABS} />

      <Card>
        {save.isSuccess ? <Alert tone="success">Settings saved.</Alert> : null}

        <form onSubmit={form.handleSubmit((values) => save.mutate(values))} noValidate>
          {data?.map((setting) => (
            <FormGroup key={setting.key} label={setting.key.replace(/_/g, ' ')}>
              <Input defaultValue={setting.value ?? ''} {...form.register(setting.key)} />
            </FormGroup>
          ))}
          <Button type="submit" variant="gold" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save Settings'}
          </Button>
        </form>
      </Card>

      <Card>
        <h3 className="card-title">Mail</h3>
        <Button
          variant="outline-gold"
          onClick={() => testEmail.mutate('')}
          disabled={testEmail.isPending}
        >
          Send test email
        </Button>
      </Card>
    </>
  );
}
